import { useContext } from "react";
import { Flex, Box } from "rebass/styled-components";
import styled from "styled-components";
import useTranslation from "next-translate/useTranslation";

import Link from "../common/Link";
import Heading from "../common/Heading";
import CityServiceDescription from "../common/CityServiceDescription";
import { BlogPosts } from "../home/Blog";
import countryServiceTypes from "../../stories/fixtures/countryServiceTypes";
import { LocaleContext } from "../locale";
import { SearchQuery } from "../../generated/graphql";

import HowItWorks from "../../components/sitterprofile/HowItWorks";
import { Benefits } from "../../components/sitterprofile/Contact";

interface Props {
  title?: string;
  introHtml?: string;
  contentHtml?: string;
  city?: SearchQuery["search"]["city"];
  serviceType?: SearchQuery["search"]["serviceType"];
  showCityServiceDescription?: boolean;
  topCities?: SearchQuery["search"]["topCities"];
  query?: Record<string, any>;
  blogPosts?: any[];
}

const CleanList = styled.ul`
  margin: 0;
  padding: 0;

  li {
    list-style: none;
    margin: 8px 0;
  }
`;

const Item = styled.li`
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  font-weight: ${({ theme }) => theme.fontWeights["book"]};
  a {
    color: ${({ theme }) => theme.colors.labradorBlack};
    text-decoration: none;
  }
`;

interface CityLinkProps {
  city: Props["topCities"][0];
  serviceType?: Props["serviceType"];
}

const OtherCities = ({
  title,
  introHtml,
  contentHtml,
  city,
  serviceType,
  showCityServiceDescription,
  topCities,
  blogPosts,
  showHowItWorksAndBenefits
}: Props) => {
  let nearbyCities = [];
  let name = null;
  if (city) {
    nearbyCities = city.nearbyCities;
    name = city.name;
  }

  const { t: tCommon } = useTranslation("common");
  const t = (key: string, values?) => tCommon(`otherCities.${key}`, values);
  const { country } = useContext(LocaleContext);

  function CityServiceLink({ city, serviceType }: CityLinkProps) {
    const { country } = useContext(LocaleContext);

    return (
      <Item>
        {/* TODO: change this for SEO friendly URls */}
        <Link
          title={t("serviceIn", {
            service: tCommon(`serviceType${serviceType.id}SeoName`),
            city: city.name
          })}
          href={{
            pathname: "/search/cityservice",
            query: {
              // ...query,
              // dogs: dogsParam,
              city: city.slug,
              service: process.env.serviceRoutes[country][
                serviceType.routeServiceParam
              ].toString()
              // serviceTypeId: null,
              // pid: null,
              // lat: null,
              // lng: null,
              // near: null
            }
          }}
        >
          <Heading
            as="h3"
            fontSize={3}
            lineHeight={1.5}
          >{`${city.name} (${city.activeCaregiversCount})`}</Heading>
        </Link>
      </Item>
    );
  }

  function CityLink({ city }: CityLinkProps) {
    return (
      <Item>
        <Link
          title={t("caregiversIn", { city: city.name })}
          href={{
            pathname: "/search/city",
            query: {
              city: city.slug
            }
          }}
        >
          <Heading as="h3" fontSize={3} lineHeight={1.5}>
            {`${city.name} (${city.activeCaregiversCount})`}
          </Heading>
        </Link>
      </Item>
    );
  }

  return (
    <Box>
      {showCityServiceDescription && (
        <Box py={[3, 3, 5]}>
          <CityServiceDescription
            title={title}
            introHtml={introHtml}
            contentHtml={contentHtml}
            city={city}
            serviceType={serviceType}
          />
        </Box>
      )}

      {showHowItWorksAndBenefits && (
        <Flex
          mt={[2, 3]}
          pt={[4, 6]}
          mb={[1, 3]}
          pb={[3, 4]}
          px={[1, 0]}
          sx={{
            borderTop: "default",
            borderBottom: "default"
          }}
          flexDirection={["column", "row"]}
        >
          <Box width={["100%", "50%"]} mr={[0, 8]} mb={[3, 0]}>
            <HowItWorks />
          </Box>

          <Box width={["100%", "50%"]}>
            <Benefits />
          </Box>
        </Flex>
      )}

      {blogPosts && <BlogPosts posts={blogPosts} />}

      <Flex flexWrap="wrap" pt={[0, 0, 5]} mx="auto">
        {nearbyCities.length > 0 && (
          <Box my={[3, 0]} width={[1, 1 / 2]}>
            <Heading as="h2" fontSize={5} fontWeight="book" mb={2}>
              {!serviceType &&
                city &&
                t("otherCities", {
                  city: name
                })}
              {serviceType &&
                city &&
                t(`otherCitiesServiceType${serviceType.id}`, {
                  city: name
                })}
              {serviceType &&
                !city &&
                t(`serviceType${serviceType.id}InOtherCities`, {
                  city: name
                })}
            </Heading>
            <CleanList>
              {nearbyCities.map((city, key) =>
                serviceType ? (
                  <CityServiceLink
                    city={city}
                    key={key}
                    serviceType={serviceType}
                  />
                ) : (
                  <CityLink city={city} key={key} />
                )
              )}
            </CleanList>
          </Box>
        )}
        {topCities && topCities.length > 0 && (
          <Box my={[3, 0]} width={[1, 1 / 2]}>
            <Heading as="h2" fontSize={5} fontWeight="book" mb={2}>
              {!serviceType
                ? t("topCities")
                : t(`topCitiesServiceType${serviceType.id}`)}
            </Heading>
            <CleanList>
              {topCities.map((city, key) =>
                serviceType ? (
                  <CityServiceLink
                    city={city}
                    key={key}
                    serviceType={serviceType}
                  />
                ) : (
                  <CityLink city={city} key={key} />
                )
              )}
            </CleanList>
          </Box>
        )}
        <Box my={[3, 0]} width={[1, 1 / 2]}>
          <Heading as="h2" fontSize={5} fontWeight="book" pb={2}>
            {city
              ? t("servicesFor", {
                  city: name
                })
              : t("servicesForYourGudog")}
          </Heading>
          <CleanList>
            {countryServiceTypes
              .filter(countryServiceType => {
                return (
                  !serviceType ||
                  serviceType.id != countryServiceType.serviceType.id
                );
              })
              .filter(countryServiceType => {
                return (
                  !countryServiceType.onlyInAvailableCountries ||
                  countryServiceType.onlyInAvailableCountries.indexOf(country) >
                    -1
                );
              })
              .map(({ serviceType }, key) => (
                <Item key={key}>
                  {/* TODO: change this for SEO friendly URls */}
                  {city ? (
                    <Link
                      href={{
                        pathname: "/search/cityservice",
                        query: {
                          // ...query,
                          city: city.slug,
                          service:
                            process.env.serviceRoutes[country][
                              serviceType.routeServiceParam
                            ]
                        }
                      }}
                    >
                      <Heading as="h3" fontSize={3} lineHeight={1.5}>
                        {tCommon(`serviceType${serviceType.id}In`, {
                          city: name
                        })}
                      </Heading>
                    </Link>
                  ) : (
                    <Link
                      href={{
                        pathname: "/search/service",
                        query: {
                          // ...query,
                          service:
                            process.env.serviceRoutes[country][
                              serviceType.routeServiceParam
                            ]
                        }
                      }}
                    >
                      <Heading as="h3" fontSize={3} lineHeight={1.5}>
                        {tCommon(`serviceType${serviceType.id}SeoName`, {
                          city: name
                        })}
                      </Heading>
                    </Link>
                  )}
                </Item>
              ))}
          </CleanList>
        </Box>
      </Flex>
    </Box>
  );
};

export default OtherCities;
